// @ts-nocheck
// EOB review stage for the Create-from-PDF overlay (PRD Phase 5c).
// Renders the EOB-shaped contract (claim_ref / patient / payer + the
// adjudication table) and a required "Not on this EOB" panel that blocks
// Continue until the user fills the fields an EOB cannot carry (billing
// NPI/TIN, per-line place_of_service). Built from the claims `shared`
// primitives. NO scan snippets / raw OCR (snippet guardrail).
import { Chip, Field, Input, Section } from '../shared';
import humanizeFieldLabel from '../shared/humanizeFieldLabel';

// Confirmed-contract entity blocks shown as editable scalar field groups.
const EOB_SCALAR_ENTITIES = [
  ['claim_ref', 'Claim Reference'],
  ['patient', 'Patient'],
  ['payer', 'Payer'],
];

function leafValue(leaf) {
  return leaf && typeof leaf === 'object' ? (leaf.value ?? '') : (leaf ?? '');
}

// Pull the displayable string out of a per-line scalar leaf (or a raw value).
function lineCell(line, field) {
  const leaf = line && line[field];
  return leafValue(leaf);
}

// Flatten carc_codes ([{code, description}] with leaf-shaped members) to a
// short "CODE" chip label and a joined reason string.
function lineCarc(line) {
  const codes = Array.isArray(line && line.carc_codes) ? line.carc_codes : [];
  return codes
    .map((c) => leafValue(c && c.code))
    .filter(Boolean);
}

/**
 * @param {{
 *   confirmed: object,            // confirmed EOB contract
 *   blockingMissing: string[],    // dotted keys + 'adjudication_lines.place_of_service'
 *   setLeaf: (entity, field, value) => void,
 *   setLinePlaceOfService: (index, value) => void,
 * }} props
 */
export default function EOBReview({ confirmed, blockingMissing, setLeaf, setLinePlaceOfService }) {
  const lines = Array.isArray(confirmed.adjudication_lines) ? confirmed.adjudication_lines : [];

  // The required "Not on this EOB" panel covers the dotted scalar keys
  // (billing_provider.npi / .tax_id) plus per-line place_of_service.
  const scalarRequired = (blockingMissing || []).filter((k) => k !== 'adjudication_lines.place_of_service');
  const posRequired = (blockingMissing || []).includes('adjudication_lines.place_of_service');

  const billing = confirmed.billing_provider || {};

  return (
    <div data-testid="eob-review">
      {EOB_SCALAR_ENTITIES.map(([entity, label]) => {
        const block = confirmed[entity];
        if (!block || typeof block !== 'object' || Array.isArray(block)) return null;
        const fields = Object.keys(block);
        if (!fields.length) return null;
        return (
          <Section key={entity} icon="hash" title={label}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {fields.map((field) => (
                <Field key={field} label={humanizeFieldLabel(field)}>
                  <Input
                    value={leafValue(block[field])}
                    onChange={(e) => setLeaf(entity, field, e.target.value)}
                    aria-label={`${entity}.${field}`}
                  />
                </Field>
              ))}
            </div>
          </Section>
        );
      })}

      {(scalarRequired.length > 0 || posRequired) && (
        <Section
          icon="search-x"
          title="Not on this EOB — needed to build a claim"
          subtitle="EOBs don't carry full CMS-1500 detail. Fill these required fields to make the extracted claim billable."
        >
          <div
            data-testid="eob-required-status"
            style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 13, color: '#b42318', fontWeight: 600 }}
          >
            <Chip tone="denied" icon="alert-triangle">
              required
            </Chip>
            Required to create the claim
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {scalarRequired.map((key) => {
              const [, field] = key.split('.');
              const missing = leafValue(billing[field]) === '';
              return (
                <Field key={key} label={humanizeFieldLabel(key)} required error={missing ? 'required' : null}>
                  <Input
                    value={leafValue(billing[field])}
                    onChange={(e) => setLeaf('billing_provider', field, e.target.value)}
                    aria-label={key}
                  />
                </Field>
              );
            })}
          </div>

          {posRequired && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                Place of service (per line)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {lines.map((line, i) => {
                  const cpt = lineCell(line, 'cpt_hcpcs_code') || `Line ${i + 1}`;
                  const pos = lineCell(line, 'place_of_service');
                  return (
                    <Field key={i} label={`${cpt} — place of service`} required error={pos === '' ? 'required' : null}>
                      <Input
                        value={pos}
                        onChange={(e) => setLinePlaceOfService(i, e.target.value)}
                        aria-label={`adjudication_lines.${i}.place_of_service`}
                      />
                    </Field>
                  );
                })}
              </div>
            </div>
          )}
        </Section>
      )}

      <Section icon="scale" title="Adjudication & Denial">
        <div style={{ border: '1px solid #eee', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="eob-adjudication-table">
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['CPT', 'Billed', 'Allowed', 'Paid', 'CARC', 'Reason'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      padding: '9px 12px',
                      textAlign: i >= 1 && i <= 3 ? 'right' : 'left',
                      fontWeight: 700,
                      fontSize: 11,
                      textTransform: 'uppercase',
                      color: '#666',
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '12px', color: '#999', textAlign: 'center' }}>
                    No adjudication lines were extracted.
                  </td>
                </tr>
              )}
              {lines.map((line, i) => {
                const carcs = lineCarc(line);
                return (
                  <tr key={i} style={{ background: i % 2 ? '#fafafa' : '#fff' }}>
                    <td style={{ padding: '9px 12px', fontWeight: 700 }}>{lineCell(line, 'cpt_hcpcs_code') || '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right' }}>{lineCell(line, 'billed_amount') || '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right' }}>{lineCell(line, 'allowed_amount') || '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right' }}>{lineCell(line, 'paid_amount') || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      {carcs.length ? (
                        carcs.map((c) => (
                          <Chip key={c} tone="denied">
                            {c}
                          </Chip>
                        ))
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#666' }}>{lineCell(line, 'denial_reason') || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
